import type { ReactNode } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import type { ClaimantProjection, DeductibleSource } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

/**
 * Claimant-grid cells for the Document Review redesign (#7435, artboard 2a).
 * Columns: Claimant (relationship sub-line) · YTD (single figure) · Deductible
 * (amount + source) · On approve (chip + over-deductible note) · Source.
 */

/** Reviewer-facing relationship label (AC1). */
export const relationshipLabel = (rel: string | null | undefined): string | null => {
  switch (rel) {
    case "self":
      return "Employee";
    case "spouse":
      return "Spouse";
    case "dependent":
      return "Child";
    case "other":
      return "Other";
    default:
      return null;
  }
};

/** Sub-label under the deductible amount (AC2/AC5). */
export const deductibleSourceLabel = (source: DeductibleSource | string | undefined): string | null => {
  switch (source) {
    case "lasered":
      return "Laser";
    case "specific":
      return "Specific";
    case "family":
      return "Family";
    // Conditional-laser engine (#3502) sources, through the same resolver.
    // Wording per #3512 AC1 (PO 2026-10-08): the same words the Lasered
    // Claimants tab's source badge uses, so the two screens agree.
    case "conditional_laser":
      return "Conditional";
    case "modification":
      return "Modification";
    case "contract_laser":
      return "Contract";
    case "review_decision":
      return "Review decision";
    case "":
    case undefined:
      return null;
    default:
      // A source the UI has no label for yet: show it generically rather
      // than leave an amount with no provenance (!4924 review).
      return formatToTitleCase(String(source));
  }
};

/**
 * Match tag beside the name (2026-10-15): "New" when approving creates the
 * claimant, "Matched by name" when the match is a soft merge onto a row with no
 * member ID (worth a look — a false positive merges two people). Nothing for an
 * ordinary match: existing is the normal case, and a tag on most rows is noise.
 * Beside the name rather than in the sub-line so it reads at a glance and the
 * sub-line keeps the family context (relationship · anchor · lines).
 */
const MatchTag = ({ status }: { status: ClaimantProjection["match_status"] | undefined }) => {
  if (status !== "new" && status !== "matched_backfill") return null;
  const isNew = status === "new";
  return (
    <Box
      component="span"
      data-testid="claimant-match-tag"
      title={
        isNew
          ? "Not on file for this client — approving creates the claimant."
          : "Matched an existing claimant by name; approving writes this Member ID onto it. Verify before approving."
      }
      sx={{
        ml: 0.75,
        px: 0.5,
        height: 16,
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "4px",
        border: "1px solid",
        borderColor: isNew ? "secondary.main" : "warning.main",
        color: isNew ? "secondary.main" : "warning.dark",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        textTransform: "uppercase",
        lineHeight: 1,
        verticalAlign: "middle",
        flexShrink: 0,
      }}
    >
      {isNew ? "New" : "Matched by name"}
    </Box>
  );
};

/**
 * Claimant name with the relationship sub-line: "Spouse · Dana Ortiz · 3 claim
 * lines" (anchor name shown for family members), or "Spouse · subscriber not
 * found · 3 claim lines" in a warning tint while the family link is pending.
 * The member ID is a hover title on the name, not a column (AC1). A match tag
 * sits beside the name for new and name-matched claimants.
 */
export const ClaimantNameCell = ({
  name,
  memberId,
  projection,
  lineCount,
  lineCountPartial = false,
}: {
  name: ReactNode;
  memberId?: string | null;
  projection: ClaimantProjection | undefined;
  lineCount?: number;
  /** Loaded-set count on a paged document → "N+ claim lines". */
  lineCountPartial?: boolean;
}) => {
  const rel = relationshipLabel(projection?.claimant_relationship);
  const pending = !!projection?.is_pending_family_link;
  const anchor = projection?.family_subscriber_name;
  const isMemberOfFamily = !!anchor && projection?.claimant_relationship !== "self";
  const parts: string[] = [];
  if (rel) parts.push(rel);
  if (pending) parts.push("subscriber not found");
  else if (isMemberOfFamily) parts.push(anchor as string);
  if (typeof lineCount === "number") {
    const n = lineCountPartial ? `${lineCount}+` : String(lineCount);
    parts.push(`${n} claim ${lineCount === 1 && !lineCountPartial ? "line" : "lines"}`);
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <Box
          component="span"
          title={memberId ? `Member ID ${memberId}` : undefined}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {name}
        </Box>
        <MatchTag status={projection?.match_status} />
      </Box>
      {parts.length > 0 && (
        <Typography
          variant="caption"
          noWrap
          sx={{ color: pending ? "warning.dark" : "text.secondary", lineHeight: 1.3 }}
        >
          {parts.join(" · ")}
        </Typography>
      )}
    </Box>
  );
};

/** Header for the Deductible column with the AC2 tooltip. */
export const DeductibleHeader = ({ policyLabel }: { policyLabel?: string }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    Deductible
    <HintIcon
      size="xs"
      hint={`The specific deductible on ${policyLabel || "this policy"}, or the claimant's laser. Claim lines outside the policy dates don't count toward it.`}
    />
  </Box>
);

/** Header for the single YTD column: "YTD" with "as of <date>" beneath (AC4). */
export const YTDHeader = ({ asOfLabel }: { asOfLabel?: string }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
      <span>YTD</span>
      {asOfLabel && (
        <Typography
          component="span"
          variant="caption"
          sx={{ textTransform: "none", fontWeight: 400, color: "text.secondary" }}
        >
          as of {asOfLabel}
        </Typography>
      )}
    </Box>
    <HintIcon
      size="xs"
      hint="The carrier's reported total as of the paid-through date. It becomes the claimant's official total on approve. Claimants with no reported total show the sum of their claim lines plus anything already on file."
    />
  </Box>
);

/**
 * Deductible the row is measured against: "$500,000" over "Laser", "$250,000"
 * over "Specific", or the family amount over "Family" (AC2/AC5). A pending
 * family member shows "—" / "Not linked" because no threshold applies yet.
 */
export const DeductibleCell = ({
  projection,
  isLoading,
}: {
  projection: ClaimantProjection | undefined;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  if (!projection) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  if (projection.is_pending_family_link) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
        <Typography variant="caption" sx={{ color: "warning.dark", lineHeight: 1.3 }}>
          Not linked
        </Typography>
      </Box>
    );
  }
  const amount = projection.applicable_deductible ?? 0;
  const source = deductibleSourceLabel(projection.deductible_source);
  // Thresholds are whole dollars in practice ("$500,000", AC2); keep cents
  // only when the policy actually carries them.
  const amountText = Number.isInteger(amount)
    ? formatUSD(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : formatUSD(amount);
  if (!(amount > 0)) {
    return (
      <Tooltip title="No specific deductible or laser applies on this policy." arrow>
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {amountText}
      </Typography>
      {source && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {source}
        </Typography>
      )}
    </Box>
  );
};

/**
 * "$32,058.22 over deductible" — only on rows that create or add to a stop-
 * loss claim (AC3). Rows under the deductible carry no sub-line; distance to
 * the deductible is deliberately not shown (decided 2026-10-13). On a family
 * pool the family total is the figure compared.
 */
export const overDeductibleNote = (projection: ClaimantProjection | undefined): string | null => {
  if (!projection) return null;
  const isStopLossRow = projection.will_create_sl_claim || !!projection.will_update_sl_claim_id;
  if (!isStopLossRow) return null;
  const ded = projection.applicable_deductible ?? 0;
  if (!(ded > 0)) return null;
  // Family rows compare the POOL total. On a windowed projection the family
  // figures are withheld (ADR-045 2026-09-08 amendment) — row YTD against the
  // family amount would be a wrong number shown confidently, so say nothing.
  if (projection.deductible_source === "family" && typeof projection.family_ytd_after !== "number") return null;
  const measured =
    projection.deductible_source === "family" && typeof projection.family_ytd_after === "number"
      ? projection.family_ytd_after
      : projection.ytd_after;
  const over = measured - ded;
  if (!(over > 0)) return null;
  const prefix =
    projection.deductible_source === "family" && typeof projection.family_ytd_after === "number"
      ? `Family ${formatUSD(projection.family_ytd_after)} · `
      : "";
  return `${prefix}${formatUSD(over)} over deductible`;
};
